import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { segmentsApi } from "./api";
import { SEGMENT_OPTIONS } from "./segments";
import type { SegmentCatalog } from "../types/api";

export type SegmentSelectItem = { value: string; label: string; description?: string | null };
export type SegmentSelectGroup = { group: string; items: SegmentSelectItem[] };

function catalogToGroupedOptions(catalog: SegmentCatalog | undefined): SegmentSelectGroup[] {
  if (!catalog?.groups?.length) {
    return [{ group: "Segments", items: SEGMENT_OPTIONS }];
  }
  return catalog.groups.map((g) => ({
    group: g.category_label,
    items: g.segments.map((s) => ({ value: s.code, label: s.label, description: s.description })),
  }));
}

function catalogToFlatOptions(catalog: SegmentCatalog | undefined): SegmentSelectItem[] {
  if (!catalog?.options?.length) return SEGMENT_OPTIONS;
  return catalog.options.map((s) => ({ value: s.code, label: s.label, description: s.description }));
}

/** Authenticated catalog for recruiter/HR/admin segment pickers. */
export function useSegmentCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["segment-catalog"],
    queryFn: () => segmentsApi.catalog(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const groupedOptions = useMemo(() => catalogToGroupedOptions(query.data), [query.data]);
  const flatOptions = useMemo(() => catalogToFlatOptions(query.data), [query.data]);
  const labels = query.data?.labels;
  return { ...query, groupedOptions, flatOptions, labels };
}

/** Public catalog for signup / unauthenticated pickers. */
export function usePublicSegmentCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["public-segment-catalog"],
    queryFn: () => segmentsApi.publicCatalog(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const groupedOptions = useMemo(() => catalogToGroupedOptions(query.data), [query.data]);
  const flatOptions = useMemo(() => catalogToFlatOptions(query.data), [query.data]);
  const labels = query.data?.labels;
  return { ...query, groupedOptions, flatOptions, labels };
}

/** Build flat MultiSelect data from intake public info (this recruiter's specialties first). */
export function intakeSegmentsToFlatOptions(intake: {
  recruiter_segments?: { value: string; label: string; description?: string | null }[];
  other_segments?: { value: string; label: string; description?: string | null }[];
  segments?: { value: string; label: string; description?: string | null }[];
}): SegmentSelectItem[] {
  const recruiterSegs = intake.recruiter_segments ?? [];
  const otherSegs = intake.other_segments ?? [];
  const source = recruiterSegs.length || otherSegs.length ? [...recruiterSegs, ...otherSegs] : intake.segments ?? [];
  return source.map((s) => ({ value: s.value, label: s.label, description: s.description }));
}

/** @deprecated kept for any lingering imports — segments are flat now, prefer intakeSegmentsToFlatOptions. */
export function intakeSegmentsToGroupedOptions(intake: {
  recruiter_segments?: { value: string; label: string; description?: string | null }[];
  other_segments?: { value: string; label: string; description?: string | null }[];
  segments?: { value: string; label: string; description?: string | null }[];
}): SegmentSelectGroup[] {
  const groups: SegmentSelectGroup[] = [];
  const recruiterSegs = intake.recruiter_segments ?? [];
  const otherSegs = intake.other_segments ?? [];
  if (recruiterSegs.length) {
    groups.push({
      group: "This recruiter's specialties",
      items: recruiterSegs.map((s) => ({ value: s.value, label: s.label, description: s.description })),
    });
  }
  if (otherSegs.length) {
    groups.push({
      group: "Other roles",
      items: otherSegs.map((s) => ({ value: s.value, label: s.label, description: s.description })),
    });
  }
  if (!groups.length && intake.segments?.length) {
    groups.push({
      group: "Role interests",
      items: intake.segments.map((s) => ({ value: s.value, label: s.label, description: s.description })),
    });
  }
  return groups;
}
